import React, { useState, useEffect } from 'react';
import { 
  MessageSquarePlus, Plus, Search, Filter, Clock, User, Calendar,
  MoreVertical, Edit3, Trash2, X, Loader2, CheckCircle2, Circle,
  AlertCircle, ChevronRight, Star, Send, MessageCircle
} from 'lucide-react';
import { useUserEmail } from '../../context/UserAuthContext';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const REQUEST_TYPES = {
  content: { label: 'Content Request', color: 'bg-blue-500/20 text-blue-400' },
  availability: { label: 'Availability', color: 'bg-green-500/20 text-green-400' },
  approval: { label: 'Approval Needed', color: 'bg-yellow-500/20 text-yellow-400' },
  feedback: { label: 'Feedback', color: 'bg-purple-500/20 text-purple-400' },
  general: { label: 'General', color: 'bg-gray-500/20 text-gray-400' }
};

const REQUEST_STATUS = {
  pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-500/20 text-blue-400', icon: Circle },
  completed: { label: 'Completed', color: 'bg-green-500/20 text-green-400', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/20 text-red-400', icon: X }
};

const PRIORITY_COLORS = {
  urgent: 'bg-red-500/20 text-red-400 border-red-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  low: 'bg-green-500/20 text-green-400 border-green-500/30'
};

const AEClientRequests = () => {
  const userEmail = useUserEmail();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    request_type: 'general',
    priority: 'medium',
    due_date: ''
  });

  useEffect(() => {
    if (userEmail) {
      checkRole();
      fetchRequests();
    }
  }, [userEmail]);

  const checkRole = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/user-role?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      setIsAdmin(data.role === 'admin');
    } catch (err) {
      console.error('Error checking role:', err);
    }
  };

  const fetchRequests = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/client-requests?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (data.success) {
        setRequests(data.data);
      }
    } catch (err) {
      console.error('Error fetching requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.title) return;
    setSaving(true);
    
    try {
      const res = await fetch(`${API}/api/always-elevate/client-requests?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        setShowCreateModal(false);
        resetForm();
        fetchRequests();
      }
    } catch (err) {
      console.error('Error creating request:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (requestId, newStatus) => {
    try {
      const res = await fetch(`${API}/api/always-elevate/client-requests/${requestId}?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      const data = await res.json();
      if (data.success) {
        fetchRequests();
        if (selectedRequest?.id === requestId) {
          setSelectedRequest({ ...selectedRequest, status: newStatus });
        }
      }
    } catch (err) {
      console.error('Error updating request:', err);
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;
    
    try {
      const res = await fetch(`${API}/api/always-elevate/client-requests/${requestId}?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        fetchRequests();
        setSelectedRequest(null);
      }
    } catch (err) {
      console.error('Error deleting request:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      request_type: 'general',
      priority: 'medium',
      due_date: ''
    });
  };

  const filteredRequests = requests.filter(request => {
    if (searchQuery && !request.title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (filterType !== 'all' && request.request_type !== filterType) {
      return false;
    }
    if (filterStatus !== 'all' && request.status !== filterStatus) {
      return false;
    }
    return true;
  });

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    in_progress: requests.filter(r => r.status === 'in_progress').length,
    completed: requests.filter(r => r.status === 'completed').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ae-client-requests-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-6">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#C4A052] text-sm font-medium tracking-wider">ALWAYS ELEVATE</p>
              <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Anton, sans-serif' }} data-testid="ae-client-requests-title">
                Client Requests
              </h1>
              <p className="text-gray-400 text-sm mt-1">Manage requests from Akayleb</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors"
              data-testid="create-request-btn"
            >
              <Plus className="w-4 h-4" />
              New Request
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-black border border-white/20 rounded-xl p-4 transition-all hover:border-blue-500/50 hover:scale-[1.02]">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <MessageSquarePlus className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-gray-400">Total Requests</p>
              </div>
            </div>
          </div>
          <div className="bg-black border border-white/20 rounded-xl p-4 transition-all hover:border-yellow-500/50 hover:scale-[1.02]">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pending}</p>
                <p className="text-xs text-gray-400">Pending</p>
              </div>
            </div>
          </div>
          <div className="bg-black border border-white/20 rounded-xl p-4 transition-all hover:border-blue-500/50 hover:scale-[1.02]">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Circle className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.in_progress}</p>
                <p className="text-xs text-gray-400">In Progress</p>
              </div>
            </div>
          </div>
          <div className="bg-black border border-white/20 rounded-xl p-4 transition-all hover:border-green-500/50 hover:scale-[1.02]">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.completed}</p>
                <p className="text-xs text-gray-400">Completed</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search requests..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-black border border-white/20 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#C4A052]"
              data-testid="request-search"
            />
          </div>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="bg-black border border-white/20 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C4A052]"
          >
            <option value="all">All Types</option>
            {Object.entries(REQUEST_TYPES).map(([key, val]) => (
              <option key={key} value={key}>{val.label}</option>
            ))}
          </select>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-black border border-white/20 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C4A052]"
          >
            <option value="all">All Status</option>
            {Object.entries(REQUEST_STATUS).map(([key, val]) => (
              <option key={key} value={key}>{val.label}</option>
            ))}
          </select>
        </div>

        {/* Requests List */}
        {filteredRequests.length === 0 ? (
          <div className="bg-black border border-white/20 rounded-xl p-12 text-center">
            <MessageSquarePlus className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">No Requests Found</h3>
            <p className="text-gray-500 text-sm mb-4">
              {searchQuery || filterType !== 'all' || filterStatus !== 'all'
                ? 'Try adjusting your filters'
                : 'Create a new request to get started'}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Request
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="requests-list">
            {filteredRequests.map(request => {
              const type = REQUEST_TYPES[request.request_type] || REQUEST_TYPES.general;
              const status = REQUEST_STATUS[request.status] || REQUEST_STATUS.pending;
              const StatusIcon = status.icon;
              
              return (
                <div
                  key={request.id}
                  onClick={() => setSelectedRequest(request)}
                  className={`bg-white/5 border rounded-xl p-4 cursor-pointer ${
                    selectedRequest?.id === request.id ? 'border-[#C4A052]' : 'border-white/10'
                  }`}
                  data-testid={`request-card-${request.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`p-2 rounded-lg ${status.color}`}>
                        <StatusIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold truncate">{request.title}</h3>
                          <span className={`px-2 py-0.5 rounded text-xs ${type.color}`}>
                            {type.label}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs border ${PRIORITY_COLORS[request.priority] || PRIORITY_COLORS.medium}`}>
                            {request.priority}
                          </span>
                        </div>
                        {request.description && (
                          <p className="text-sm text-gray-400 truncate">{request.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {request.requested_by?.split('@')[0] || 'Unknown'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(request.created_at).toLocaleDateString()}
                          </span>
                          {request.due_date && (
                            <span className="flex items-center gap-1 text-yellow-400">
                              <Clock className="w-3 h-3" />
                              Due: {new Date(request.due_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Request Detail Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold">{selectedRequest.title}</h3>
              <button 
                onClick={() => setSelectedRequest(null)} 
                className="p-2 hover:bg-white/10 rounded-lg"
                data-testid="close-request-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Status & Type */}
              <div className="flex items-center gap-3 mb-6">
                <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${REQUEST_STATUS[selectedRequest.status]?.color}`}>
                  {REQUEST_STATUS[selectedRequest.status]?.label}
                </span>
                <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${REQUEST_TYPES[selectedRequest.request_type]?.color}`}>
                  {REQUEST_TYPES[selectedRequest.request_type]?.label}
                </span>
                <span className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${PRIORITY_COLORS[selectedRequest.priority]}`}>
                  {selectedRequest.priority} priority
                </span>
              </div>

              {/* Description */}
              {selectedRequest.description && (
                <div className="mb-6">
                  <p className="text-xs text-gray-500 mb-1">Description</p>
                  <p className="text-gray-300">{selectedRequest.description}</p>
                </div>
              )}

              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Requested By</p>
                  <p className="text-sm">{selectedRequest.requested_by || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Created</p>
                  <p className="text-sm">{new Date(selectedRequest.created_at).toLocaleDateString()}</p>
                </div>
                {selectedRequest.due_date && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Due Date</p>
                    <p className="text-sm">{new Date(selectedRequest.due_date).toLocaleDateString()}</p>
                  </div>
                )}
              </div>

              {/* Admin Response */}
              {selectedRequest.admin_response && (
                <div className="mb-6 bg-white/5 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Admin Response</p>
                  <p className="text-gray-300">{selectedRequest.admin_response}</p>
                </div>
              )}

              {/* Status Update (Admin only) */}
              {isAdmin && (
                <div className="mb-6">
                  <p className="text-xs text-gray-500 mb-2">Update Status</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(REQUEST_STATUS).map(([key, val]) => (
                      <button
                        key={key}
                        onClick={() => handleUpdateStatus(selectedRequest.id, key)}
                        className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                          selectedRequest.status === key
                            ? val.color
                            : 'bg-white/5 text-gray-400 hover:bg-white/10'
                        }`}
                      >
                        {val.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-white/10 flex justify-between">
              {isAdmin && (
                <button
                  onClick={() => handleDelete(selectedRequest.id)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                  data-testid="delete-request-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              )}
              <button
                onClick={() => setSelectedRequest(null)}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors ml-auto"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-lg w-full">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold">New Request</h3>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  placeholder="Request title"
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052] resize-none h-24"
                  placeholder="Describe the request in detail"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Type</label>
                  <select
                    value={formData.request_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, request_type: e.target.value }))}
                    className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  >
                    {Object.entries(REQUEST_TYPES).map(([key, val]) => (
                      <option key={key} value={key}>{val.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  >
                    <option value="urgent">Urgent</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Due Date</label>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                  className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-white/10 flex justify-end gap-3">
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                disabled={saving}
              >
                Cancel
              </button>
              <button 
                onClick={handleCreate}
                disabled={!formData.title || saving}
                className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AEClientRequests;
